import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { QueryConfig } from '@nohara-dxdev/react-core';
import { useOidc, useScopeIds } from '@nohara-dxdev/react-oidc';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

import type { FlagTargetState } from '@nohara-dxdev/feature-flags';

export interface MeFeatureFlagsResponse {
  flags: FlagTargetState[];
}

export const getMeFeatureFlagsQueryKey = (
  orgId: string | null | undefined,
  userId: string | null | undefined,
) => ['feature-flags', 'me', orgId ?? '', userId ?? ''] as const;

export const getMeFeatureFlags = async (orgId: string): Promise<MeFeatureFlagsResponse> => {
  const { data } = await api.get<unknown, ApiResponse<MeFeatureFlagsResponse>>(
    `/orgs/${orgId}/me/feature-flags`,
  );
  return data;
};

export const getMeFeatureFlagsQueryOptions = (
  orgId: string | null | undefined,
  userId: string | null | undefined,
) =>
  queryOptions({
    queryKey: getMeFeatureFlagsQueryKey(orgId, userId),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => getMeFeatureFlags(orgId!),
    enabled: !!orgId && !!userId,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

interface UseFeatureFlagsMeOptions {
  queryConfig?: QueryConfig<typeof getMeFeatureFlagsQueryOptions>;
}

export const useFeatureFlagsMe = ({ queryConfig = {} }: UseFeatureFlagsMeOptions = {}) => {
  const context = useScopeIds();
  const { orgId } = context;
  const { userId } = useOidc();
  return useQuery({
    ...getMeFeatureFlagsQueryOptions(orgId, userId),
    ...queryConfig,
  });
};
